import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { initializeGA4, isGA4Loaded } from '@/lib/analytics';
import { getAnalyticsSettings } from '@/config/analytics';
import { useCookiePreferences } from '@/hooks/useCookiePreferences';

interface AnalyticsContextType {
  isAnalyticsLoaded: boolean;
  isAnalyticsConfigured: boolean;
  shouldTrack: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  isAnalyticsLoaded: false,
  isAnalyticsConfigured: false,
  shouldTrack: false
});

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const { isConfigured, shouldTrack } = getAnalyticsSettings();
  const { isLoaded: cookiePreferencesLoaded, canUseAnalytics } = useCookiePreferences();
  const [isLoaded, setIsLoaded] = React.useState(false);

  useEffect(() => {
    if (!isConfigured) {
      // console.warn('Analytics not configured, skipping initialization'); // Removed for production
      return;
    }

    // Wait for cookie preferences to load
    if (!cookiePreferencesLoaded) {
      return;
    }

    // Only initialize analytics if user has consented
    if (!canUseAnalytics()) {
      // console.log('Analytics disabled by user consent'); // Removed for production
      return;
    }

    if (!shouldTrack) {
      // console.log('Analytics tracking disabled for this environment'); // Removed for production
      return;
    }

    // Initialize GA4
    try {
      initializeGA4();
      
      // Check if GA4 loaded successfully
      const checkLoaded = () => {
        if (isGA4Loaded()) {
          setIsLoaded(true);
          // console.log('✅ Google Analytics 4 loaded successfully'); // Removed for production
        } else {
          // Retry after a short delay
          setTimeout(checkLoaded, 100);
        }
      };
      
      checkLoaded();
    } catch (error) {
      console.error('Failed to initialize Google Analytics 4:', error);
    }
  }, [isConfigured, shouldTrack, cookiePreferencesLoaded, canUseAnalytics]);

  const value: AnalyticsContextType = {
    isAnalyticsLoaded: isLoaded,
    isAnalyticsConfigured: isConfigured,
    shouldTrack: shouldTrack && canUseAnalytics()
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}; 